import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, MapPin, Activity, Users, AlertTriangle, Route, Layers } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { loadGoogleMaps, isGoogleMapsLoaded, getGoogleMapsApi } from '../lib/googleMaps';
import { MapErrorBoundary } from './MapErrorBoundary';
import i18n from '../i18n/config';

interface ActiveTech {
  user_id: string;
  display_name: string | null;
  user_email: string;
  report_id: string | null;
  job_site_id: string | null;
  job_site_name: string | null;
  job_site_lat: number | null;
  job_site_lng: number | null;
  status_type: string | null;
  status_label: string | null;
  status_color: string | null;
  started_at: string | null;
  last_activity: string;
  latitude: number | null;
  longitude: number | null;
  accuracy_m: number | null;
  located_at: string | null;
}

interface FeedItem {
  id: string;
  created_at: string;
  category: string;
  message: string;
  user_email: string;
  display_name: string | null;
  job_site_name: string | null;
}

interface LiveResponse {
  technicians: ActiveTech[];
  feed: FeedItem[];
}

interface TrailPoint {
  latitude: number;
  longitude: number;
  accuracy_m: number | null;
  created_at: string;
  category: string;
}

type DeviceType = 'controller' | 'zone' | 'water_meter' | 'double_check';

interface PropertyDevice {
  id: string;
  type: DeviceType;
  label: string;
  job_site_id: string;
  latitude: number;
  longitude: number;
}

const POLL_INTERVAL_MS = 20_000;

// Device-type marker colors (match PropertyMap so the dashboard reads the same
// as the per-property map). Devices are drawn as diamonds, not circles, so they
// never get confused with the round technician markers even where colors overlap.
const DEVICE_COLOR: Record<DeviceType, string> = {
  controller: '#2563eb',
  zone: '#0891b2',
  water_meter: '#16a34a',
  double_check: '#d97706',
};

const DEVICE_LABEL_KEY: Record<DeviceType, string> = {
  controller: 'dashboard.devController',
  zone: 'dashboard.devZone',
  water_meter: 'dashboard.devWaterMeter',
  double_check: 'dashboard.devDoubleCheck',
};

// SVG path for a unit diamond, scaled by the marker's `scale`.
const DIAMOND_PATH = 'M 0 -1 L 1 0 L 0 1 L -1 0 Z';

function techName(t: { display_name: string | null; user_email: string }): string {
  return t.display_name || t.user_email || '—';
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffSec = Math.round((Date.now() - then) / 1000);
  const rtf = new Intl.RelativeTimeFormat(i18n.language || 'en', { numeric: 'auto' });
  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return rtf.format(-diffMin, 'minute');
  const diffHr = Math.round(diffMin / 60);
  return rtf.format(-diffHr, 'hour');
}

export function InspectionsDashboard() {
  const { t } = useTranslation('common');
  const [data, setData] = useState<LiveResponse>({ technicians: [], feed: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [trail, setTrail] = useState<TrailPoint[]>([]);
  const [showTrail, setShowTrail] = useState(true);
  const [devices, setDevices] = useState<PropertyDevice[]>([]);
  const [showDevices, setShowDevices] = useState(true);

  // Poll the live endpoint.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiClient.get<LiveResponse>('/api/dashboard/live');
        if (!cancelled) {
          setData(res);
          setError(null);
          setLastUpdated(new Date());
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('dashboard.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [t]);

  // Lazily (re)fetch the breadcrumb trail for the selected technician. Re-runs
  // when new live data arrives so the trail keeps extending as heartbeats land.
  useEffect(() => {
    if (!selectedUserId) {
      setTrail([]);
      return;
    }
    let cancelled = false;
    apiClient
      .get<{ points: TrailPoint[] }>(`/api/dashboard/trail/${selectedUserId}`)
      .then((r) => {
        if (!cancelled) setTrail(r.points ?? []);
      })
      .catch(() => {
        if (!cancelled) setTrail([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUserId, data]);

  // Devices and the property pin are shown ONLY for the selected technician's
  // active inspection — never every active tech's devices at once. Resolve the
  // selected tech and fetch just their property's devices; clear when nothing
  // is selected (default view = technician locations only).
  const selectedTech = data.technicians.find((tc) => tc.user_id === selectedUserId) ?? null;
  const selectedSiteId = selectedTech?.job_site_id ?? null;
  const selectedProperty =
    selectedTech && selectedTech.job_site_lat != null && selectedTech.job_site_lng != null
      ? {
          lat: selectedTech.job_site_lat,
          lng: selectedTech.job_site_lng,
          name: selectedTech.job_site_name ?? '',
        }
      : null;

  useEffect(() => {
    if (!selectedSiteId) {
      setDevices([]);
      return;
    }
    let cancelled = false;
    apiClient
      .get<{ devices: PropertyDevice[] }>(
        `/api/dashboard/devices?job_site_ids=${encodeURIComponent(selectedSiteId)}`,
      )
      .then((r) => {
        if (!cancelled) setDevices(r.devices ?? []);
      })
      .catch(() => {
        if (!cancelled) setDevices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSiteId]);

  const technicians = data.technicians;
  const withLocation = technicians.filter(
    (tc) => tc.latitude != null && tc.longitude != null,
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary flex items-center gap-2">
            <MapPin size={24} />
            {t('dashboard.title')}
          </h1>
          <p className="text-sm text-theme-secondary mt-0.5">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-1.5 text-theme-secondary">
            <Users size={16} />
            {t('dashboard.activeCount', { count: technicians.length })}
          </span>
          {lastUpdated && (
            <span className="inline-flex items-center gap-1.5 text-theme-secondary">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              {t('dashboard.updated', { time: relativeTime(lastUpdated.toISOString()) })}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Map */}
        <div className="lg:col-span-2 flex flex-col gap-2">
          <DashboardMap
            technicians={withLocation}
            devices={showDevices ? devices : []}
            property={selectedProperty}
            selectedUserId={selectedUserId}
            trail={showTrail ? trail : []}
            onSelect={(id) => setSelectedUserId((cur) => (cur === id ? null : id))}
          />
          <div className="flex items-center justify-between text-xs text-theme-secondary px-1">
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTrail}
                  onChange={(e) => setShowTrail(e.target.checked)}
                  className="rounded"
                />
                <Route size={13} />
                {t('dashboard.showTrails')}
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showDevices}
                  onChange={(e) => setShowDevices(e.target.checked)}
                  className="rounded"
                />
                <Layers size={13} />
                {t('dashboard.showDevices')}
              </label>
            </div>
            {selectedUserId && trail.length > 0 && (
              <span>{t('dashboard.trailPoints', { count: trail.length })}</span>
            )}
          </div>
        </div>

        {/* Right column: active techs + activity feed */}
        <div className="flex flex-col gap-4 min-h-0">
          <ActiveTechList
            technicians={technicians}
            selectedUserId={selectedUserId}
            onSelect={(id) => setSelectedUserId((cur) => (cur === id ? null : id))}
          />
          <ActivityFeed feed={data.feed} loading={loading} />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Map                                                                 */
/* ------------------------------------------------------------------ */

function staleness(lastActivityIso: string): 'live' | 'recent' | 'stale' {
  const ageMin = (Date.now() - new Date(lastActivityIso).getTime()) / 60000;
  if (ageMin <= 5) return 'live';
  if (ageMin <= 20) return 'recent';
  return 'stale';
}

const STALENESS_COLOR: Record<'live' | 'recent' | 'stale', string> = {
  live: '#16a34a', // green — moving now
  recent: '#d97706', // amber — a few minutes ago
  stale: '#6b7280', // grey — quiet a while
};

interface DashboardMapProps {
  technicians: ActiveTech[];
  devices: PropertyDevice[];
  property: { lat: number; lng: number; name: string } | null;
  selectedUserId: string | null;
  trail: TrailPoint[];
  onSelect: (id: string) => void;
}

function DashboardMap(props: DashboardMapProps) {
  return (
    <MapErrorBoundary>
      <DashboardMapInner {...props} />
    </MapErrorBoundary>
  );
}

function DashboardMapInner({
  technicians,
  devices,
  property,
  selectedUserId,
  trail,
  onSelect,
}: DashboardMapProps) {
  const { t } = useTranslation('common');
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const deviceMarkersRef = useRef<google.maps.Marker[]>([]);
  const propertyMarkerRef = useRef<google.maps.Marker | null>(null);
  const trailRef = useRef<google.maps.Polyline | null>(null);
  const trailDotsRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const didFitRef = useRef(false);
  const [mapReady, setMapReady] = useState(isGoogleMapsLoaded());
  const [error, setError] = useState<string | null>(null);

  // Keep latest onSelect without re-binding markers.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // Latest technicians without forcing the zoom effect to re-run every poll.
  const techsRef = useRef(technicians);
  techsRef.current = technicians;

  useEffect(() => {
    loadGoogleMaps()
      .then(() => {
        // Resolving is not proof the API is usable — check the global itself.
        if (isGoogleMapsLoaded()) setMapReady(true);
        else setError(t('dashboard.mapLoadError'));
      })
      .catch((err) => {
        console.error('Failed to load Google Maps for the live dashboard:', err);
        setError(t('dashboard.mapLoadError'));
      });
  }, [t]);

  // Create the map once.
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return;
    // Never touch the bare `google` global: it throws a ReferenceError rather
    // than reading as undefined when the third-party script isn't there.
    const maps = getGoogleMapsApi();
    if (!maps) {
      setError(t('dashboard.mapLoadError'));
      return;
    }
    try {
      mapInstanceRef.current = new maps.Map(mapRef.current, {
        center: { lat: 39.5, lng: -98.35 }, // continental US fallback until markers load
        zoom: 4,
        mapTypeId: maps.MapTypeId.HYBRID,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
      });
      infoWindowRef.current = new maps.InfoWindow();
    } catch (err) {
      console.error('Failed to create the live dashboard map:', err);
      setError(t('dashboard.mapLoadError'));
    }
  }, [mapReady, t]);

  // Render technician markers whenever the data changes.
  useEffect(() => {
    const map = mapInstanceRef.current;
    const maps = getGoogleMapsApi();
    if (!mapReady || !map || !maps) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const bounds = new maps.LatLngBounds();
    technicians.forEach((tc) => {
      if (tc.latitude == null || tc.longitude == null) return;
      const pos = { lat: tc.latitude, lng: tc.longitude };
      const isSelected = tc.user_id === selectedUserId;
      // Color reflects how fresh the plotted *position* is (the dot is where the
      // tech was at located_at), falling back to overall activity.
      const color = STALENESS_COLOR[staleness(tc.located_at || tc.last_activity)];
      const marker = new maps.Marker({
        position: pos,
        map,
        title: techName(tc),
        zIndex: isSelected ? 1000 : 500,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: isSelected ? 11 : 8,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: isSelected ? '#111827' : '#ffffff',
          strokeWeight: isSelected ? 3 : 2,
        },
      });
      marker.addListener('click', () => {
        onSelectRef.current(tc.user_id);
        const iw = infoWindowRef.current;
        if (iw) {
          iw.setContent(techInfoHtml(tc, t));
          iw.open(map, marker);
        }
      });
      markersRef.current.push(marker);
      bounds.extend(pos);
    });

    // Fit to markers once on first load; afterwards leave the manager's view
    // alone so the 20s poll doesn't yank the map around.
    if (!didFitRef.current && technicians.length > 0) {
      didFitRef.current = true;
      if (technicians.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(18);
      } else {
        map.fitBounds(bounds, { top: 48, bottom: 48, left: 48, right: 48 });
      }
    }
  }, [mapReady, technicians, selectedUserId, t]);

  // Render the property's defined-location devices (controllers, zones, water
  // meters, double checks) as diamonds. These are the equipment of the property
  // each active tech is inspecting — drawn beneath the techs and trail so the
  // live actors stay on top.
  useEffect(() => {
    const map = mapInstanceRef.current;
    const maps = getGoogleMapsApi();
    if (!mapReady || !map || !maps) return;

    deviceMarkersRef.current.forEach((m) => m.setMap(null));
    deviceMarkersRef.current = [];

    devices.forEach((dev) => {
      const color = DEVICE_COLOR[dev.type];
      const marker = new maps.Marker({
        position: { lat: dev.latitude, lng: dev.longitude },
        map,
        title: dev.label,
        zIndex: 150,
        icon: {
          path: DIAMOND_PATH,
          scale: dev.type === 'zone' ? 4.5 : 6,
          fillColor: color,
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 1.5,
        },
      });
      marker.addListener('click', () => {
        const iw = infoWindowRef.current;
        if (iw) {
          iw.setContent(deviceInfoHtml(dev, t(DEVICE_LABEL_KEY[dev.type]), color));
          iw.open(map, marker);
        }
      });
      deviceMarkersRef.current.push(marker);
    });
  }, [mapReady, devices, t]);

  // Render the property pin for the selected technician's active inspection.
  // Only present when a tech is selected (property is null otherwise).
  useEffect(() => {
    const map = mapInstanceRef.current;
    const maps = getGoogleMapsApi();
    if (!mapReady || !map || !maps) return;

    if (propertyMarkerRef.current) {
      propertyMarkerRef.current.setMap(null);
      propertyMarkerRef.current = null;
    }
    if (!property) return;

    const marker = new maps.Marker({
      position: { lat: property.lat, lng: property.lng },
      map,
      title: property.name || t('dashboard.property'),
      zIndex: 600,
      icon: {
        path: 'M 0 0 C -7 -16 -13 -19 -13 -27 a 13 13 0 1 1 26 0 c 0 8 -6 11 -13 27 z',
        fillColor: '#dc2626',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1,
        anchor: new maps.Point(0, 0),
        labelOrigin: new maps.Point(0, -27),
      },
    });
    marker.addListener('click', () => {
      const iw = infoWindowRef.current;
      if (iw) {
        const esc = (s: string) =>
          s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
        iw.setContent(
          `<div style="font-family:sans-serif;min-width:150px;">
            <div style="font-weight:700;font-size:13px;color:#111;">${esc(t('dashboard.property'))}</div>
            ${property.name ? `<div style="font-size:12px;color:#374151;">${esc(property.name)}</div>` : ''}
            <div style="font-size:11px;color:#9ca3af;margin-top:4px;">${property.lat.toFixed(6)}, ${property.lng.toFixed(6)}</div>
          </div>`,
        );
        iw.open(map, marker);
      }
    });
    propertyMarkerRef.current = marker;
  }, [mapReady, property, t]);

  // Zoom/pan to a technician when they're selected.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapReady || !map || !selectedUserId) return;
    const tc = techsRef.current.find((t) => t.user_id === selectedUserId);
    if (tc && tc.latitude != null && tc.longitude != null) {
      map.panTo({ lat: tc.latitude, lng: tc.longitude });
      map.setZoom(19);
    }
  }, [selectedUserId, mapReady]);

  // Render the breadcrumb trail for the selected technician.
  useEffect(() => {
    const map = mapInstanceRef.current;
    const maps = getGoogleMapsApi();
    if (!mapReady || !map || !maps) return;

    if (trailRef.current) {
      trailRef.current.setMap(null);
      trailRef.current = null;
    }
    trailDotsRef.current.forEach((d) => d.setMap(null));
    trailDotsRef.current = [];

    if (trail.length < 1) return;

    const path = trail.map((p) => ({ lat: p.latitude, lng: p.longitude }));

    if (path.length >= 2) {
      trailRef.current = new maps.Polyline({
        path,
        map,
        strokeColor: '#2563eb',
        strokeOpacity: 0.9,
        strokeWeight: 3,
        zIndex: 400,
      });
    }

    // The polyline above carries every point; dot markers are decimated so a
    // dense (continuously-tracked) trail doesn't drop hundreds of markers. Keep
    // start + latest, plus an evenly-sampled subset of the middle (cap ~40).
    const MAX_DOTS = 40;
    const lastIdx = trail.length - 1;
    const step = Math.max(1, Math.ceil(trail.length / MAX_DOTS));
    trail.forEach((p, idx) => {
      const isStart = idx === 0;
      const isEnd = idx === lastIdx;
      if (!isStart && !isEnd && idx % step !== 0) return;
      const dot = new maps.Marker({
        position: { lat: p.latitude, lng: p.longitude },
        map,
        zIndex: 450,
        title: isStart ? t('dashboard.trailStart') : isEnd ? t('dashboard.trailLatest') : undefined,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: isStart || isEnd ? 6 : 3,
          fillColor: isStart ? '#111827' : isEnd ? '#2563eb' : '#60a5fa',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: isStart || isEnd ? 2 : 1,
        },
      });
      trailDotsRef.current.push(dot);
    });
  }, [mapReady, trail, t]);

  if (error) {
    return (
      <div className="w-full h-[calc(100vh-16rem)] min-h-[480px] rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 flex items-center justify-center text-red-600 dark:text-red-400 text-sm p-6 text-center">
        {error}
      </div>
    );
  }

  if (!mapReady) {
    return (
      <div className="w-full h-[calc(100vh-16rem)] min-h-[480px] rounded-lg border border-line bg-subtle flex items-center justify-center text-theme-secondary text-sm gap-2">
        <RefreshCw size={16} className="animate-spin" />
        {t('dashboard.mapLoading')}
      </div>
    );
  }

  return (
    <div className="relative w-full h-[calc(100vh-16rem)] min-h-[480px] rounded-lg overflow-hidden border border-line shadow-sm">
      <div ref={mapRef} className="w-full h-full" />
      {technicians.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 pointer-events-none">{/* theme-allow: light scrim over dark satellite map tiles */}
          <div className="text-center text-gray-500 text-sm">{/* theme-allow: dark text on the light scrim */}
            <MapPin size={28} className="mx-auto mb-2 text-gray-400" />{/* theme-allow */}
            {t('dashboard.noActiveLocations')}
          </div>
        </div>
      )}
      {/* Legend */}
      <div className="absolute bottom-2 left-2 bg-surface rounded-md shadow px-3 py-2 text-xs text-theme-primary space-y-1">
        <LegendRow color={STALENESS_COLOR.live} label={t('dashboard.legendLive')} />
        <LegendRow color={STALENESS_COLOR.recent} label={t('dashboard.legendRecent')} />
        <LegendRow color={STALENESS_COLOR.stale} label={t('dashboard.legendStale')} />
        {(property || devices.length > 0) && (
          <div className="border-t border-line my-1" />
        )}
        {property && <LegendRow color="#dc2626" label={t('dashboard.property')} />}
        {devices.length > 0 && (
          <>
            {(['controller', 'zone', 'water_meter', 'double_check'] as DeviceType[])
              .filter((type) => devices.some((d) => d.type === type))
              .map((type) => (
                <LegendRow
                  key={type}
                  color={DEVICE_COLOR[type]}
                  label={t(DEVICE_LABEL_KEY[type])}
                  diamond
                />
              ))}
          </>
        )}
      </div>
    </div>
  );
}

function LegendRow({ color, label, diamond }: { color: string; label: string; diamond?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`w-2.5 h-2.5 border border-white shadow-sm ${diamond ? 'rotate-45' : 'rounded-full'}`}
        style={{ background: color }}
      />
      <span>{label}</span>
    </div>
  );
}

function techInfoHtml(tc: ActiveTech, t: (k: string, o?: Record<string, unknown>) => string): string {
  const esc = (s: string) =>
    s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
  const acc = tc.accuracy_m != null ? `±${Math.round(tc.accuracy_m)}m` : '—';
  const status = tc.status_label ? esc(tc.status_label) : t('dashboard.statusDraft');
  return `
    <div style="font-family: sans-serif; min-width: 180px;">
      <div style="font-weight:700;font-size:13px;color:#111;margin-bottom:2px;">${esc(techName(tc))}</div>
      ${tc.job_site_name ? `<div style="font-size:12px;color:#374151;">${esc(tc.job_site_name)}</div>` : ''}
      <div style="font-size:11px;color:#6b7280;margin-top:4px;">
        ${status} · ${t('dashboard.accuracy')} ${acc}
      </div>
      <div style="font-size:11px;color:#9ca3af;margin-top:2px;">
        ${t('dashboard.lastSeen')} ${esc(relativeTime(tc.located_at || tc.last_activity))}
      </div>
    </div>`;
}

function deviceInfoHtml(d: PropertyDevice, typeLabel: string, color: string): string {
  const esc = (s: string) =>
    s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
  return `
    <div style="font-family: sans-serif; min-width: 160px;">
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px;">
        <span style="width:9px;height:9px;background:${color};transform:rotate(45deg);display:inline-block;"></span>
        <span style="font-weight:700;font-size:13px;color:#111;">${esc(typeLabel)}</span>
      </div>
      <div style="font-size:12px;color:#374151;">${esc(d.label)}</div>
      <div style="font-size:11px;color:#9ca3af;margin-top:4px;">
        ${d.latitude.toFixed(6)}, ${d.longitude.toFixed(6)}
      </div>
    </div>`;
}

/* ------------------------------------------------------------------ */
/* Active technician list                                              */
/* ------------------------------------------------------------------ */

function ActiveTechList({
  technicians,
  selectedUserId,
  onSelect,
}: {
  technicians: ActiveTech[];
  selectedUserId: string | null;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="bg-surface rounded-lg border border-line shadow-sm">
      <div className="px-4 py-3 border-b border-line flex items-center gap-2">
        <Users size={16} className="text-theme-secondary" />
        <h2 className="text-sm font-semibold text-theme-primary">{t('dashboard.activeTechs')}</h2>
      </div>
      {technicians.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-theme-secondary">{t('dashboard.noActive')}</div>
      ) : (
        <ul className="divide-y divide-line max-h-56 overflow-y-auto">
          {technicians.map((tc) => {
            const selected = tc.user_id === selectedUserId;
            const color = STALENESS_COLOR[staleness(tc.last_activity)];
            return (
              <li key={tc.user_id}>
                <button
                  onClick={() => onSelect(tc.user_id)}
                  className={`w-full text-left px-4 py-2.5 transition-colors ${selected ? 'bg-blue-50 dark:bg-blue-500/10' : 'hover:bg-subtle'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-white shadow-sm" style={{ background: color }} />
                    <span className="font-medium text-sm text-theme-primary truncate">{techName(tc)}</span>
                    {tc.latitude == null && (
                      <span className="text-[10px] uppercase tracking-wide text-theme-secondary ml-auto">{t('dashboard.noFix')}</span>
                    )}
                  </div>
                  <div className="text-xs text-theme-secondary mt-0.5 ml-[18px] truncate">
                    {tc.job_site_name || t('dashboard.noSite')} · {relativeTime(tc.last_activity)}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Activity feed                                                       */
/* ------------------------------------------------------------------ */

const CATEGORY_LABEL_KEY: Record<string, string> = {
  inspection_workflow_opened: 'dashboard.evt.opened',
  inspection_save_started: 'dashboard.evt.saving',
  inspection_save_succeeded: 'dashboard.evt.saved',
  inspection_save_failed: 'dashboard.evt.saveFailed',
  inspection_save_loop_blocked: 'dashboard.evt.saveLoopBlocked',
  inspection_submit_blocked: 'dashboard.evt.submitBlocked',
  inspection_tab_changed: 'dashboard.evt.tabChanged',
  inspection_location_heartbeat: 'dashboard.evt.location',
  inspection_location_left_property: 'dashboard.evt.leftProperty',
  gps_capture_started: 'dashboard.evt.gpsStarted',
  gps_capture_succeeded: 'dashboard.evt.gpsSucceeded',
  gps_capture_retry: 'dashboard.evt.gpsRetry',
  gps_capture_failed: 'dashboard.evt.gpsFailed',
  gps_capture_unavailable: 'dashboard.evt.gpsUnavailable',
};

function ActivityFeed({ feed, loading }: { feed: FeedItem[]; loading: boolean }) {
  const { t } = useTranslation('common');
  const friendly = useCallback(
    (item: FeedItem) => {
      const key = CATEGORY_LABEL_KEY[item.category];
      return key ? t(key) : item.category.replace(/_/g, ' ');
    },
    [t],
  );

  return (
    <div className="bg-surface rounded-lg border border-line shadow-sm flex flex-col min-h-0 flex-1">
      <div className="px-4 py-3 border-b border-line flex items-center gap-2">
        <Activity size={16} className="text-theme-secondary" />
        <h2 className="text-sm font-semibold text-theme-primary">{t('dashboard.activityFeed')}</h2>
        {loading && <RefreshCw size={13} className="animate-spin text-theme-secondary ml-auto" />}
      </div>
      {feed.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-theme-secondary">{t('dashboard.noActivity')}</div>
      ) : (
        <ul className="divide-y divide-line overflow-y-auto" style={{ maxHeight: 360 }}>
          {feed.map((item) => (
            <li key={item.id} className="px-4 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm text-theme-primary font-medium truncate">
                  {item.display_name || item.user_email}
                </span>
                <span className="text-[11px] text-theme-secondary flex-shrink-0">{relativeTime(item.created_at)}</span>
              </div>
              <div className="text-xs text-theme-secondary truncate">
                {friendly(item)}
                {item.job_site_name ? ` · ${item.job_site_name}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
